import { useState } from 'react';
import _ from 'lodash';
import { LinearProgress } from '@material-ui/core';
import { useFormContext } from 'react-hook-form';
import { Trans } from '@lingui/macro';
import FormSection from '../../form/FormSection';
import { LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD, TEST_PASSWORD_FIELD, TEST_USERNAME_FIELD } from './SsoAdminConstants';
import NgpTextField from '../../form/NgpTextField';
import { AUTO_COMPLETE_DEFAULT } from '../../../utilities/FormUtilities';
import NgpPasswordField from '../../form/NgpPasswordField';
import { useDataService } from '../../../services/ApiDataService';
import { buildVerifyResult, processVerifyResults } from './LdapVerifyHelper';
import { useStyles } from './LdapVerifyStyles';
import { getLdapDefaults } from './SsoAdminHelper';
import NgpFormButton from '../../form/NgpFormButton';

const verifyAPIUrl = '/ipm/v1/admin/userAuthentication/ldap/testConfig';

function buildVerifyConfig(formData, dbData) {
    const buildingConfig = _.defaults(_.cloneDeep(_.get(formData, 'ldap', {})), dbData.ldap, getLdapDefaults());
    _.set(buildingConfig, 'bindCredentials', _.get(formData, LDAP_ENCRYPTED_BIND_CREDENTIALS_FIELD));
    _.set(buildingConfig, 'encryptedBindCredentials', null);
    return {
        ldapConfig: _.omit(buildingConfig, ['testUser']),
        testUser: _.get(buildingConfig, 'testUser'),
    };
}

export default function LdapVerify({ formData }) {
    const classes = useStyles();
    const { getValues, formState } = useFormContext();
    const [verifyResultHtml, setVerifyResultHtml] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const dataService = useDataService();

    const handleVerifyClick = () => {
        setVerifying(true);
        const { ldapConfig, testUser } = buildVerifyConfig(getValues(), formData);
        return dataService
            .post(verifyAPIUrl, { ldapConfig, testUser })
            .then((results) => processVerifyResults(results, { ldapConfig, testUser }))
            .then((result) => setVerifyResultHtml(buildVerifyResult(result, classes)))
            .finally(() => setVerifying(false));
    };

    return (
        <FormSection
            title={<Trans>Verify</Trans>}
            subText={
                <Trans>
                    This section allows you to verify your Active Directory configuration and optionally validate a user
                    within Active Directory.
                </Trans>
            }
        >
            <NgpTextField
                fieldName={TEST_USERNAME_FIELD}
                label={<Trans>Username (optional)</Trans>}
                inputProps={{
                    size: 60,
                    maxLength: 4096,
                    autoComplete: AUTO_COMPLETE_DEFAULT,
                }}
            />

            <NgpPasswordField
                fieldName={TEST_PASSWORD_FIELD}
                label={<Trans>Password (optional)</Trans>}
                inputProps={{ size: 60, maxLength: 4096 }}
                togglePasswordVisibility
            />

            <div className={classes.verifyButtonContainer}>
                <NgpFormButton disabled={!formState.isValid} onClick={handleVerifyClick}>
                    <Trans>Verify</Trans>
                </NgpFormButton>
            </div>

            <div className={classes.verifyTestResults}>
                {verifying && <LinearProgress />}
                {!verifying && verifyResultHtml && <>{verifyResultHtml}</>}
            </div>
        </FormSection>
    );
}
